import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ExportCatalogUerBaseCostHistoryAsExcelQuery } from 'gql/graphql';
import { exportCatalogUERBaseCostHistoryAsExcelGql } from 'pages/Catalogs/CatalogUERBaseCost/catalogUerBaseCostQueries';
import { downloadURI } from 'utils/downloadDataURI';
import type { TActionCommonInterface } from 'utils/types';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

interface IUseExportHistoryProps {
    catalogId: string | undefined;
    dateFilterValues: {
        endDateIso: string | undefined;
        startDateIso: string | undefined;
    };
}

const requestId = 'exportCatalogUERBaseCostHistoryAsExcel';

export const useExportHistoryAsExcel = ({ catalogId, dateFilterValues }: IUseExportHistoryProps) => {
    const dispatch = useDispatch();

    const [resetData] = useResetStoreData({ requestId });

    const onSuccess = useCallback(
        (res: TActionCommonInterface<ExportCatalogUerBaseCostHistoryAsExcelQuery>) => {
            const { name, dataUrl } = res?.payload?.exportCatalogUERBaseCostHistoryAsExcel?.data ?? {};

            if (dataUrl && name) {
                downloadURI(dataUrl, name);
            }

            resetData(undefined);
        },
        [resetData],
    );

    const [, status, , request, , abortRequest] = useRequest({
        query: exportCatalogUERBaseCostHistoryAsExcelGql,
        requestId,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const variables = useMemo(() => {
        if (catalogId && dateFilterValues?.endDateIso && dateFilterValues?.startDateIso) {
            return {
                catalogUniqueId: catalogId,
                startDate: dateFilterValues.startDateIso,
                endDate: dateFilterValues.endDateIso,
            };
        }

        return undefined;
    }, [catalogId, dateFilterValues.endDateIso, dateFilterValues.startDateIso]);

    const exportHistoryHandler = useCallback(() => {
        if (variables) {
            dispatch(request(variables));
        }
    }, [dispatch, request, variables]);

    useEffect(
        () => () => {
            dispatch(abortRequest());
        },
        [abortRequest, dispatch],
    );

    const isLoading = useMemo(() => status === RequestStatus.FETCHING, [status]);

    const isHandlerDisabled = useMemo(
        () => !catalogId || !dateFilterValues?.endDateIso || !dateFilterValues?.startDateIso,
        [catalogId, dateFilterValues?.endDateIso, dateFilterValues?.startDateIso],
    );

    return {
        exportHistoryHandler,
        isExportHistoryInProgress: isLoading,
        isHandlerDisabled,
    };
};
